import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { localeName, localeShort, langChoiceKey } from '@/lib/locales';

/**
 * Language switcher + SEO hreflang for a public landing page.
 *
 * Renders nothing (and emits no hreflang) for a monolingual page, so the
 * single-language case is unchanged. When a page offers more than one locale
 * it shows a compact switcher and injects the alternate-language <link> tags
 * search engines use to serve the right language.
 *
 * URLs are path-based: the default locale lives at /p/{slug}, the others at
 * /p/{slug}/{locale} — which is what hreflang wants.
 */

function hrefFor(slug, locale, defaultLocale, origin) {
  const path = locale === defaultLocale ? `/p/${slug}` : `/p/${slug}/${locale}`;
  return { path, url: `${origin}${path}` };
}

export default function LanguageSwitcher({ slug, locales = [], activeLocale, defaultLocale = 'en', preview = false }) {
  const multi = locales.length > 1;

  // Inject/refresh hreflang alternates in <head>. Skipped in preview (a draft
  // shouldn't advertise alternates to crawlers) and for monolingual pages.
  useEffect(() => {
    if (!multi || preview || typeof document === 'undefined') return undefined;
    const origin = window.location.origin;
    const links = [];
    const add = (hreflang, url) => {
      const el = document.createElement('link');
      el.rel = 'alternate';
      el.hreflang = hreflang;
      el.href = url;
      el.dataset.i18nAlt = '1';
      document.head.appendChild(el);
      links.push(el);
    };
    locales.forEach((loc) => add(loc, hrefFor(slug, loc, defaultLocale, origin).url));
    // x-default points at the default locale.
    add('x-default', hrefFor(slug, defaultLocale, defaultLocale, origin).url);

    return () => links.forEach((el) => el.remove());
  }, [multi, preview, slug, defaultLocale, locales]);

  if (!multi) return null;

  return (
    <div
      className="fixed right-3 top-3 z-50 flex items-center gap-1 rounded-full border border-black/10 bg-white/90 px-2 py-1 shadow-sm backdrop-blur"
      data-testid="language-switcher"
    >
      <Globe className="ml-1 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      {locales.map((loc) => {
        const isActive = loc === (activeLocale || defaultLocale);
        const { path } = hrefFor(slug, loc, defaultLocale, window.location.origin);
        return (
          <Link
            key={loc}
            to={path}
            hrefLang={loc}
            aria-current={isActive ? 'true' : undefined}
            title={localeName(loc)}
            data-testid={`lang-${loc}`}
            // Record the explicit choice so auto-detect never overrides it —
            // picking "EN" must stick even on a French-language browser.
            onClick={() => { try { sessionStorage.setItem(langChoiceKey(slug), loc); } catch { /* private mode */ } }}
            className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
              isActive ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:bg-black/5'
            }`}
          >
            {localeShort(loc)}
          </Link>
        );
      })}
    </div>
  );
}
